import React from "react";
import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { SplashCursor } from '../components/SplashCursor';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from 'react-i18next';

let THEME = {};
const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: 'var(--bg-deep-void)', height: '100%', position: 'relative' },
  backgroundContainer: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 },
  contentContainer: {
    flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24,
    position: 'relative', zIndex: 2, backgroundColor: THEME.overlay, backdropFilter: 'blur(3px)',
  },
  logoContainer: {
    width: 180, height: 180, borderRadius: 90, backgroundColor: THEME.bgSurface,
    justifyContent: 'center', alignItems: 'center', marginBottom: 48,
    boxShadow: '0 0 40px rgba(77, 166, 255, 0.3)', backdropFilter: 'blur(20px)',
    borderWidth: 1, borderColor: THEME.border,
  },
  logo: { width: 180, height: 180, borderRadius: 90 },
  title: {
    fontSize: 64, fontWeight: '800', color: THEME.textPrimary, marginBottom: 16,
    textAlign: 'center', fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em',
    textShadow: '0 0 30px rgba(77, 166, 255, 0.6)',
  },
  subtitle: { fontSize: 28, color: 'var(--text-secondary)', marginBottom: 48, textAlign: 'center', fontWeight: '300', letterSpacing: '0.02em', maxWidth: 800 },
  descriptionContainer: {
    maxWidth: 700, padding: 32, borderRadius: 24, marginBottom: 64,
    backgroundColor: THEME.bgSurface, borderWidth: 1, borderColor: THEME.border, backdropFilter: 'blur(10px)',
  },
  description: { fontSize: 20, color: 'var(--text-primary)', lineHeight: 32, textAlign: 'center', fontWeight: '400' },
  buttonContainer: { width: '100%', maxWidth: 400, marginBottom: 80, gap: 20 },
  loginButton: {
    backgroundColor: 'var(--accent-primary)', borderRadius: 20, padding: 24, width: '100%', alignItems: 'center',
    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)', transition: 'transform 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)', cursor: 'pointer',
  },
  loginButtonText: { color: 'white', fontWeight: '700', fontSize: 20, letterSpacing: '0.05em' },
  demoButton: {
    backgroundColor: THEME.bgSurface, borderRadius: 20, padding: 22, width: '100%', alignItems: 'center',
    borderWidth: 1, borderColor: THEME.border, cursor: 'pointer', transition: 'background-color 0.2s',
  },
  demoButtonText: { color: THEME.textPrimary, fontWeight: '600', fontSize: 18, letterSpacing: '0.05em' },
  footer: { position: 'absolute', bottom: 30, alignItems: 'center', justifyContent: 'center', width: '100%' },
  footerText: { color: THEME.textMuted, fontSize: 14, marginBottom: 8 },
  footerLinks: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  footerLinkText: { color: THEME.textMuted, fontSize: 12, fontWeight: '500', cursor: 'pointer' },
  footerDivider: { color: THEME.textMuted, fontSize: 12 },
});
let styles = getStyles();

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useTranslation();
  Object.assign(THEME, theme);
  styles = getStyles();

  const handleLogin = () => {
    navigate('/login');
  };

  const handleContact = () => {
    // Open mailto link
    window.open('mailto:zohar@reactfirst.ai', '_blank');
  };

  return (
    <View style={styles.container}>
      {/* Background animation */}
      <View style={styles.backgroundContainer}>
        <SplashCursor
          BACK_COLOR={theme.splashBackColor}
          COLOR_UPDATE_SPEED={5}
          SPLAT_RADIUS={0.3}
          DENSITY_DISSIPATION={2.5}
        />
      </View>

      {/* Content overlay */}
      <View style={styles.contentContainer}>
        <View style={styles.logoContainer}>
          <Image
            source={require('../assets/images/ReactFirstR.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <div className="welcome-title">
          <Text style={styles.title}>{t('app.welcome.title')}</Text>
        </div>
        <Text style={styles.subtitle}>{t('app.welcome.subtitle')}</Text>

        <View style={styles.descriptionContainer}>
          <Text style={styles.description}>{t('app.welcome.description')}</Text>
        </View>

        <View style={styles.buttonContainer}>
          <TouchableOpacity style={styles.loginButton} onPress={handleLogin}>
            <Text style={styles.loginButtonText}>{t('app.welcome.login')}</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.demoButton} onPress={handleContact}>
            <Text style={styles.demoButtonText}>{t('app.welcome.contact_us')}</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>{t('app.welcome.footer')}</Text>
          <View style={styles.footerLinks}>
            <TouchableOpacity onPress={() => navigate('/privacy')}>
              <Text style={styles.footerLinkText}>{t('app.welcome.privacy_policy')}</Text>
            </TouchableOpacity>
            <Text style={styles.footerDivider}>•</Text>
            <TouchableOpacity onPress={() => navigate('/terms')}>
              <Text style={styles.footerLinkText}>{t('app.welcome.terms_of_use')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};


export default WelcomeScreen; 